import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

type FontWeight = 'bold' | 'medium' | 'regular' | 'semibold';
type Align = 'left' | 'right' | 'center';
type Size = 'xs' | 's' | 'm' | 'lg' | 'xl' |'2xl' | '3xl' | '4xl';

interface Props extends HTMLAttributes<HTMLElement>{
    fontWeight?: FontWeight;
    color?: string;
    align?: Align;
    ellipsis?: boolean;
    size?: Size;
    as?: 'p' | 'span' | 'div' | 'h1' |'h2' | 'h3' | 'h4' |'h5' | 'h6';
}

const FONT_WEIGHTS: Record<FontWeight, number> = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};

const FONT_SIZES: Record<Size, number> = {
    xs: 10,
    s: 12,
    m: 14,
    lg: 16,
    xl: 18,
    '2xl': 20,
    '3xl': 22,
    '4xl': 24,
};

function Text(props: Props) {
    const {
        children, fontWeight, color, align = 'left', ellipsis = false,
        as = 'p',
        size = 'm', ...rest
    } = props;
    const { i18n: { language } } = useTranslation();
    return (
        <Component
            as={as}
            lang={language}
            fontWeight={fontWeight}
            textColor={color}
            align={align}
            ellipsis={ellipsis}
            size={size}
            {...rest}>
            {children}
        </Component>
    );
}

const STYLE_PROPS = ['as', 'fontWeight', 'textColor', 'align', 'ellipsis', 'size'];

const Component = styled('p', {
    shouldForwardProp: (prop) => !STYLE_PROPS.includes(prop),
})<{
    fontWeight?: FontWeight;
    textColor?: string;
    align: Align;
    ellipsis: boolean;
    size: Size;
}>`
    color: ${({ textColor }) => textColor || 'var(--color-gray9)'};
    font-size: ${({ size }) => FONT_SIZES[size]}px;
    font-weight: ${({ fontWeight }) => (fontWeight ? FONT_WEIGHTS[fontWeight] : 400)};
    line-height: 1.4;
    word-break: keep-all;
    text-align: ${({ align }) => align};
    ${({ ellipsis }) => ellipsis && css`
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `}
`;

export default Text;
